import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Upload, RefreshCw, Play, Pause, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { BriefBulletsAfrikaService } from "@/services/briefBullets/briefBulletsAfrikaService";
import { SupabaseStorageService } from "@/services/photo/supabaseStorageService";
import { supabase } from "@/integrations/supabase/client";
import { PromptViewer } from "./PromptViewer";
import { BRIEFR_BULLETS_PROMPTS, SCHEDULE_INFO } from "@/constants/briefBulletsPrompts";

export function BriefBulletsAfrikaManager() {
  const queryClient = useQueryClient();
  const [photoUrl, setPhotoUrl] = useState<string>("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const { data: taskStatus, isLoading: taskLoading } = useQuery({
    queryKey: ["briefBulletsAfrikaTask"],
    queryFn: BriefBulletsAfrikaService.getTaskStatus,
    refetchInterval: 30000,
  });

  const { data: weekStatus, isLoading: weekLoading } = useQuery({
    queryKey: ["briefBulletsAfrikaWeek"],
    queryFn: BriefBulletsAfrikaService.getThisWeekStatus,
    refetchInterval: 30000,
  });

  const { data: publishHistory = [] } = useQuery({
    queryKey: ["briefBulletsAfrikaHistory"],
    queryFn: () => BriefBulletsAfrikaService.getPublishHistory(4),
  });

  const { data: taskLogs = [] } = useQuery({
    queryKey: ["briefBulletsAfrikaLogs"],
    queryFn: () => BriefBulletsAfrikaService.getTaskLogs(10),
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (taskStatus?.config?.photo_url) {
      setPhotoUrl(taskStatus.config.photo_url);
    }
  }, [taskStatus]);

  // Auto-update photo URL to new Africa map if needed
  useEffect(() => {
    const updatePhotoIfNeeded = async () => {
      if (taskStatus && taskStatus.config?.photo_url !== "/images/africa-map-new.png") {
        try {
          await BriefBulletsAfrikaService.updatePhotoUrl("/images/africa-map-new.png");
          queryClient.invalidateQueries({ queryKey: ["briefBulletsAfrikaTask"] });
          toast.success("Oppdatert til nytt Afrika-kart");
        } catch (error) {
          console.error("Failed to update photo URL:", error);
        }
      }
    };
    
    if (taskStatus) {
      updatePhotoIfNeeded();
    }
  }, [taskStatus, queryClient]);

  const publishMutation = useMutation({
    mutationFn: BriefBulletsAfrikaService.triggerManualPublish,
    onSuccess: (result) => {
      if (result.success) {
        toast.success("Afrika-artikkel publisert!");
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAfrikaWeek"] });
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAfrikaHistory"] });
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAfrikaLogs"] });
      } else {
        toast.error(result.error || "Kunne ikke publisere artikkel");
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "En feil oppstod");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (enable: boolean) =>
      BriefBulletsAfrikaService.toggleAutomaticPublishing(enable),
    onSuccess: (success, enable) => {
      if (success) {
        toast.success(
          enable
            ? "Automatisk publisering aktivert"
            : "Automatisk publisering deaktivert"
        );
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAfrikaTask"] });
      } else {
        toast.error("Kunne ikke endre status");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: BriefBulletsAfrikaService.deleteThisWeekArticle,
    onSuccess: (result) => {
      if (result.success) {
        toast.success("Artikkel slettet");
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAfrikaWeek"] });
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAfrikaHistory"] });
      } else {
        toast.error(result.error || "Kunne ikke slette artikkel");
      }
    },
  });

  const handlePhotoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const uploadedUrl = await SupabaseStorageService.uploadImage(
        file,
        "profile-images"
      );
      setPhotoUrl(uploadedUrl);

      const { error } = await supabase.rpc(
        "update_briefr_bullets_afrika_config",
        {
          new_photo_url: uploadedUrl,
        }
      );

      if (error) throw error;

      toast.success("Bilde lastet opp!");
      queryClient.invalidateQueries({ queryKey: ["briefBulletsAfrikaTask"] });
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Kunne ikke laste opp bilde");
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (taskLoading || weekLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">
          Briefr Bullets: Afrika 🌍
        </h2>
        <p className="text-muted-foreground">
          Publiseres automatisk hver torsdag kl. 05:30
        </p>
      </div>

      {/* Prompt Viewer */}
      <PromptViewer 
        taskName="Afrika"
        prompt={BRIEFR_BULLETS_PROMPTS.afrika}
        config={taskStatus?.config}
        scheduleInfo={SCHEDULE_INFO.afrika}
      />

      {/* Current Week Status */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-2">Status denne uken</h3>
        {weekStatus?.published ? (
          <div className="space-y-2">
            <p className="text-green-600">✓ Artikkel publisert</p>
            <p className="text-sm text-muted-foreground">
              Publisert:{" "}
              {weekStatus.article?.timestamp &&
                format(new Date(weekStatus.article.timestamp), "PPpp")}
            </p>
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  window.open(`/brief/${weekStatus.article?.id}`, "_blank")
                }
              >
                Vis artikkel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Slett
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground">Ikke publisert ennå</p>
        )}
      </div>

      {/* Task Configuration */}
      <div className="border rounded-lg p-4 space-y-2">
        <h3 className="font-semibold mb-2">Konfigurasjon</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Status:</span>
            <span
              className={`ml-2 font-medium ${
                taskStatus?.status === "active"
                  ? "text-green-600"
                  : "text-yellow-600"
              }`}
            >
              {taskStatus?.status === "active" ? "Aktiv" : "Pauset"}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Planlagt tid:</span>
            <span className="ml-2 font-medium">
              {taskStatus?.schedule_time || "05:30"}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Siste kjøring:</span>
            <span className="ml-2 font-medium">
              {taskStatus?.last_run
                ? format(new Date(taskStatus.last_run), "PPpp")
                : "Aldri"}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Neste kjøring:</span>
            <span className="ml-2 font-medium">
              {taskStatus?.next_run
                ? format(new Date(taskStatus.next_run), "PPpp")
                : "Ikke planlagt"}
            </span>
          </div>
        </div>
      </div>

      {/* Photo Upload */}
      <div className="border rounded-lg p-4 space-y-4">
        <h3 className="font-semibold">Profilbilde</h3>
        {photoUrl && (
          <div className="relative w-32 h-32 rounded-lg overflow-hidden border">
            <img
              src={photoUrl}
              alt="Afrika map"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div>
          <Label htmlFor="afrika-photo" className="cursor-pointer">
            <div className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <Upload className="h-4 w-4" />
              Last opp nytt bilde
            </div>
          </Label>
          <Input
            id="afrika-photo"
            type="file"
            accept="image/*"
            onChange={handlePhotoUpload}
            disabled={uploadingPhoto}
            className="hidden"
          />
          {uploadingPhoto && (
            <p className="text-sm text-muted-foreground mt-2">
              Laster opp...
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          onClick={() => publishMutation.mutate()}
          disabled={publishMutation.isPending || weekStatus?.published}
        >
          {publishMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Play className="h-4 w-4 mr-2" />
          )}
          Publiser nå
        </Button>
        <Button
          variant="outline"
          onClick={() =>
            queryClient.invalidateQueries({
              queryKey: ["briefBulletsAfrikaTask"],
            })
          }
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Oppdater
        </Button>
        <Button
          variant={taskStatus?.status === "active" ? "secondary" : "default"}
          onClick={() =>
            toggleMutation.mutate(taskStatus?.status !== "active")
          }
          disabled={toggleMutation.isPending}
        >
          {taskStatus?.status === "active" ? (
            <>
              <Pause className="h-4 w-4 mr-2" />
              Pause automatisk publisering
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Aktiver automatisk publisering
            </>
          )}
        </Button>
      </div>

      {/* Recent Publications */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Siste publikasjoner</h3>
        <div className="space-y-2">
          {publishHistory.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Ingen publikasjoner ennå
            </p>
          ) : (
            publishHistory.map((article) => (
              <div
                key={article.id}
                className="flex items-center justify-between py-2 border-b last:border-0"
              >
                <div>
                  <p className="text-sm font-medium">{article.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(article.timestamp), "PPpp")}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => window.open(`/brief/${article.id}`, "_blank")}
                >
                  Vis
                </Button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Recent Logs */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Siste kjøringer</h3>
        <div className="space-y-2">
          {taskLogs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ingen logger ennå</p>
          ) : (
            taskLogs.map((log) => (
              <div
                key={log.id}
                className="flex items-center justify-between py-2 border-b last:border-0"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs font-medium ${
                        log.status === "completed"
                          ? "text-green-600"
                          : log.status === "failed"
                          ? "text-red-600"
                          : "text-yellow-600"
                      }`}
                    >
                      {log.status === "completed"
                        ? "✓ Fullført"
                        : log.status === "failed"
                        ? "✗ Feilet"
                        : "⋯ Kjører"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(log.created_at), "PPpp")}
                    </span>
                  </div>
                  {log.execution_time_ms && (
                    <p className="text-xs text-muted-foreground">
                      Kjøretid: {(log.execution_time_ms / 1000).toFixed(2)}s
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Card>
  );
}
